import Vector from "victor";
import { postData, postJSON } from "@/utils/post";
import { uuid } from "short-uuid";
import KefirBus from "./KefirBus";
import Camera from "./Camera";
import { PDFController, PDFInfo } from "./PDFController";

const REVIEW_PROMPT_KEY = "hasAcceptedHumanReview";

export enum DragState {
  none = "none",
  dragging = "dragging",
  uploading = "uploading",
}

export interface ImageUploadControllerDelegate {
  imageUploadControllerDidReceiveImage(
    images: { size: Vector; url: string; position: Vector }[]
  ): void;
  imageUploadControllerDidReceivePDF(pdf: PDFInfo): void;
  imageUploadControllerDidReceiveDisallowedUploadRequest(): void;
}

export class ImageUploadController {
  state = new KefirBus<DragState, void>("DragState");
  mayDrag = true;
  mousePosition?: Vector;
  el?: HTMLElement;

  constructor(
    private wbid: string,
    private key: string,
    private camera: Camera,
    private pdfController: PDFController,
    private imageUploadCapability:
      | "unmoderated"
      | "approved"
      | "banned"
      | "disallowed_anonymous",
    private delegate: ImageUploadControllerDelegate
  ) {
    this.state.emit(DragState.none);
  }

  bind(el: HTMLElement) {
    this.el = el;
    el.addEventListener("dragenter", this.onDragOver);
    el.addEventListener("dragover", this.onDragOver);
    el.addEventListener("dragend", this.onStopDrag);
    el.addEventListener("dragleave", this.onStopDrag);
    el.addEventListener("drop", this.onDrop);
    document.addEventListener("paste", this.onPaste);
    el.addEventListener("mousemove", this.onMouseMove);
  }

  unbind() {
    this.el?.removeEventListener("dragenter", this.onDragOver);
    this.el?.removeEventListener("dragover", this.onDragOver);
    this.el?.removeEventListener("dragend", this.onStopDrag);
    this.el?.removeEventListener("dragleave", this.onStopDrag);
    this.el?.removeEventListener("drop", this.onDrop);
    document.removeEventListener("paste", this.onPaste);
    this.el?.removeEventListener("mousemove", this.onMouseMove);
  }

  private getShouldInterceptDragEvent(e: DragEvent): boolean {
    if ((e.dataTransfer?.files.length || 0) > 0) return true;
    return (
      new Array(...(e.dataTransfer?.items || [])).filter((i) =>
        isUploadable(i.type)
      ).length > 0
    );
  }

  private getShouldInterceptClipboardEvent(e: ClipboardEvent): boolean {
    return (
      new Array(...(e.clipboardData?.items || [])).filter((i) =>
        isUploadable(i.type)
      ).length > 0
    );
  }

  moderationGate(): boolean {
    switch (this.imageUploadCapability) {
      case "approved":
        return true;
      case "disallowed_anonymous":
        this.delegate.imageUploadControllerDidReceiveDisallowedUploadRequest();
        return false;
      case "banned":
        return false;
      case "unmoderated":
        if (localStorage[REVIEW_PROMPT_KEY] !== "true") {
          localStorage[REVIEW_PROMPT_KEY] = JSON.stringify(
            confirm(
              "Images are reviewed at random by humans to check for abuse. Is this OK with you? If not, your upload will be canceled."
            )
          );
        }
        return localStorage[REVIEW_PROMPT_KEY] === "true";
    }
  }

  onDragOver = (e: DragEvent) => {
    if (!this.mayDrag || !this.getShouldInterceptDragEvent(e)) return;
    // console.log(e.type, e.currentTarget);
    this.state.emit(DragState.dragging);
    e.preventDefault();
  };

  onStopDrag = () => {
    // console.log(e?.type, e?.currentTarget);
    this.state.emit(DragState.none);
  };

  onDrop = (e: DragEvent) => {
    if (!this.mayDrag || !this.getShouldInterceptDragEvent(e)) {
      this.onStopDrag();
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    this.mousePosition = this.point(e);
    this.onStopDrag();
    if (!this.moderationGate()) return;
    if (e.dataTransfer?.files.length) {
      this.handleBlobs(
        new Array(...e.dataTransfer.files).filter((f) => isUploadable(f.type))
      );
    } else if (e.dataTransfer?.items.length) {
      this.handleBlobs(
        new Array(...e.dataTransfer.items)
          .filter((i) => isUploadable(i.type))
          .map((item) => item.getAsFile())
          .filter((f) => f !== null) as File[]
      );
    }
  };

  onPaste = (e: ClipboardEvent) => {
    if (!this.getShouldInterceptClipboardEvent(e)) return;
    if (!this.moderationGate()) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    const blobs = new Array(...items)
      .filter((i) => isUploadable(i.type))
      .map((i) => i.getAsFile())
      .filter((f) => f !== null) as File[];
    this.handleBlobs(blobs);
  };

  onMouseMove = (e: MouseEvent) => {
    this.mousePosition = this.point(e);
  };

  async handleBlobs(blobs: File[]) {
    // Remember mouse position if we have it
    const shapePosition = (() => {
      return this.mousePosition || this.camera.origin;
    })();

    this.state.emit(DragState.uploading);

    const uploadData = async (blob: File) => {
      const imageID = uuid() + (isPDF(blob.type) ? ".pdf" : ".png");
      const requestURL = `/whiteboards/${this.wbid}/upload_image/${imageID}?key=${this.key}&content_type=${blob.type}&content_length=${blob.size}`;

      try {
        const response = await postJSON<{
          url: string;
          fields: Record<string, string>;
        }>(requestURL, {});
        await postData(response.data.url, blob, blob.type);
      } catch (e) {
        const maybeTypedError = e as unknown as {
          response?: { data?: { message?: string } };
        };
        if (maybeTypedError.response?.data?.message) {
          alert(maybeTypedError.response?.data.message);
        }
        throw e;
      }

      return `/whiteboards/${this.wbid}/images/${imageID}`;
    };

    const imagePromises: Promise<HTMLImageElement>[] = blobs
      .filter((blob) => !isPDF(blob.type))
      .map(async (blob) => loadImage(await uploadData(blob)));

    const pdfPromises: Promise<PDFInfo>[] = blobs
      .filter((blob) => isPDF(blob.type))
      .slice(0, 1)
      .map(async (blob) => this.pdfController.load(await uploadData(blob), 1));

    if (imagePromises.length) {
      const promiseResults = await Promise.allSettled(imagePromises);
      const goodResults = (
        promiseResults.filter(
          (result) => result.status === "fulfilled"
        ) as PromiseFulfilledResult<HTMLImageElement>[]
      ).map((r) => r.value);

      this.delegate.imageUploadControllerDidReceiveImage(
        goodResults.map((image) => ({
          size: new Vector(image.width, image.height),
          url: image.src,
          position: shapePosition,
        }))
      );
    }

    if (pdfPromises.length) {
      const pdf = await pdfPromises[0];
      this.delegate.imageUploadControllerDidReceivePDF(pdf);
    }

    this.state.emit(DragState.none);
  }

  point(e: MouseEvent) {
    if (!this.el) return new Vector(e.clientX, e.clientY);
    return this.camera.convertScreenToWorld(
      new Vector(e.clientX - this.el.offsetLeft, e.clientY - this.el.offsetTop)
    );
  }
}

const imageRegex = /^image\//g;
function isImage(type: string): boolean {
  return type.match(imageRegex) !== null;
}

function isPDF(type: string): boolean {
  return type === "application/pdf";
}

function isUploadable(type: string): boolean {
  return isImage(type) || isPDF(type);
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
